import Phaser from 'phaser';
import { getLevel } from '../levels';
import { AD_COPY } from '../ad-copy';
import { orderedLevelIds } from '../levels/manifest';
import { explainFailure, suggestFix } from '../sim/simulator';
import { simulate } from '../sim/simulator';
import { isBreachable, type LevelDef, type SimResult } from '../sim/types';
import { nodeStyle, wireStyle } from '../render/palette';
import { drawWatermark } from '../render/watermark';
import { getChapterForLevel } from '../levels/manifest';
import { dailyShareText } from '../daily-share';
import { isProceduralId, proceduralIndex, isDailyId, dailyKeyFromId, dailyDateLabel } from '../levels/procgen';
import { Progress, MAX_LIVES } from '../progress';
import { checkAndUnlock } from '../achievements';
import { fmtScore } from '../score';
import { Ads } from '../ads';
import { t, tf } from '../i18n';
import { SANS, UI, Panel, uiButton, clean, reducedMotion } from '../render/theme';

export class ResultScene extends Phaser.Scene {
  /** Android back button action, read by back-button.ts. */
  onBack?: () => void;
  private levelId!: string;
  private result!: SimResult;
  private isFirstClear = false;
  private scoreGain?: number;
  private runScore?: number;

  constructor() {
    super('Result');
  }

  init(data: { levelId: string; result: SimResult; isFirstClear?: boolean; scoreGain?: number; runScore?: number }) {
    this.levelId = data.levelId;
    this.result = data.result;
    this.isFirstClear = !!data.isFirstClear;
    this.scoreGain = data.scoreGain;
    this.runScore = data.runScore;
  }

  create() {
    const { width, height } = this.scale;
    const isWin = this.result.outcome === 'win';
    const endless = isProceduralId(this.levelId);
    const lives = Progress.lives();
    // Endless fail with no lives left = the run is over (offer an ad for a life).
    const runOver = endless && !isWin && lives <= 0;
    const headlineColor = isWin ? UI.signalHex : '#ff7a99';
    const accent = isWin ? UI.signal : 0xff5577;
    const calm = reducedMotion();
    const headline = runOver
      ? t('run_over_headline')
      : (isWin ? t('result_win_headline') : t('result_fail_headline'));

    this.add.rectangle(0, 0, width, height, 0x000000, 0.8).setOrigin(0).setInteractive().setDepth(0);

    // Celebration: one soft ring pair and a small burst, nothing loud.
    if (isWin && !calm) {
      this.cameras.main.flash(200, 40, 110, 80);
      for (let i = 0; i < 2; i++) {
        const ring = this.add.circle(width / 2, height * 0.45, 90, accent, 0)
          .setStrokeStyle(4, accent, 1).setBlendMode(Phaser.BlendModes.ADD).setDepth(0.5);
        this.tweens.add({
          targets: ring, radius: 560 + i * 120, alpha: { from: 0.7, to: 0 },
          duration: 1100 + i * 150, delay: i * 120, ease: 'Cubic.easeOut',
          onComplete: () => ring.destroy(),
        });
      }
      const colors = [0x66ffaa, 0x88e8ff, 0xb0ffd0];
      for (let i = 0; i < 22; i++) {
        const a = (i / 22) * Math.PI * 2 + Math.random() * 0.3;
        const dist = 280 + Math.random() * 300;
        const dot = this.add.circle(width / 2, height * 0.45, 4 + Math.random() * 3,
          colors[i % colors.length], 1).setBlendMode(Phaser.BlendModes.ADD).setDepth(0.5);
        this.tweens.add({
          targets: dot, x: width / 2 + Math.cos(a) * dist, y: height * 0.45 + Math.sin(a) * dist + 100,
          alpha: 0, duration: 800 + Math.random() * 500, ease: 'Quad.easeOut',
          onComplete: () => dot.destroy(),
        });
      }
    } else if (!isWin && !calm) {
      this.cameras.main.shake(220, 0.006);
      this.cameras.main.flash(160, 70, 20, 40);
    }

    // === CONTENT === laid out top down from a cursor, then centred as a card.
    const cardW = Math.min(width - 96, 900);
    const innerW = cardW - 120;
    const cx = width / 2;
    const items: Phaser.GameObjects.GameObject[] = [];
    let cur = 70;
    const track = <T extends Phaser.GameObjects.GameObject>(o: T): T => {
      (o as unknown as Phaser.GameObjects.Text).setDepth(2);
      items.push(o);
      return o;
    };
    const text = (s: string, size: number, color: string, gap: number, weight = '700', extra: Phaser.Types.GameObjects.Text.TextStyle = {}) => {
      const o = track(this.add.text(cx, cur, s, {
        fontFamily: SANS, fontSize: `${size}px`, color, fontStyle: weight,
        wordWrap: { width: innerW }, align: 'center', ...extra,
      }).setOrigin(0.5, 0));
      cur += o.height + gap;
      return o;
    };

    const level = getLevel(this.levelId)!;

    if (this.isFirstClear) text(clean(t('result_first_clear')), 24, UI.coolHex, 10, '800');
    text(`${level.name.toUpperCase()}  ·  ${isWin ? t('result_cleared_suffix') : t('result_aborted_suffix')}`,
      24, UI.textDim, 14, '700');
    const headlineText = text(headline, 76, headlineColor, 22, '800');

    // Lives as pips that drain, endless only.
    if (endless) {
      const g = track(this.add.graphics());
      g.setPosition(0, cur + 16);
      const gap = 48, x0 = cx - ((MAX_LIVES - 1) * gap) / 2;
      for (let i = 0; i < MAX_LIVES; i++) {
        if (i < lives) { g.fillStyle(0xff7a99, 1); g.fillCircle(x0 + i * gap, 0, 14); }
        else { g.lineStyle(3, 0xff7a99, 0.4); g.strokeCircle(x0 + i * gap, 0, 13); }
      }
      cur += 56;
    }

    const body = runOver
      ? t('run_over_body')
      : isWin
        ? tf('result_win_body_template', { ticks: this.result.totalTicks, nodes: this.collapsedCount() })
        : explainFailure(level, this.result.failure!);
    text(body, 30, UI.text, 26, '400', { lineSpacing: 10 });

    // Score count-up (endless win)
    if (isWin && endless && this.scoreGain != null) {
      const gain = this.scoreGain;
      const scoreTxt = text(`+${fmtScore(calm ? gain : 0)}`, 72, UI.warm, 4, '800');
      if (!calm) {
        const counter = { v: 0 };
        this.tweens.add({
          targets: counter, v: gain, duration: 700, delay: 250, ease: 'Cubic.easeOut',
          onUpdate: () => scoreTxt.setText(`+${fmtScore(Math.round(counter.v))}`),
          onComplete: () => scoreTxt.setText(`+${fmtScore(gain)}`),
        });
      }
      if (this.runScore != null) text(tf('result_run_total', { n: fmtScore(this.runScore) }), 26, UI.textDim, 22, '700');
      else cur += 18;
    }

    // Streak badge (endless win)
    if (isWin && endless) {
      const streak = Progress.endlessStreak();
      const milestone = streak >= 5 && (streak === 5 || streak === 10 || streak % 25 === 0);
      const label = text(tf('result_streak', { n: streak }), milestone ? 44 : 34, milestone ? UI.warm : UI.signalHex, 6, '800');
      if (milestone) {
        text(tf('result_streak_wins', { n: streak }), 24, '#ffe9b0', 14, '700');
        if (!calm) {
          label.setScale(1.2);
          this.tweens.add({ targets: label, scale: 1, duration: 320, ease: 'Back.easeOut' });
          this.cameras.main.flash(200, 90, 70, 20);
        }
      } else cur += 8;
    }

    // Daily solved badge
    if (isWin && isDailyId(this.levelId)) {
      const streak = Progress.dailyStreak();
      text(`${t('daily_solved')}  ·  ${t('daily_streak')} ×${streak}`, 32, UI.warm, 16, '800');
    }

    // Fail map: the board in miniature, using only data the simulator already returned.
    if (!isWin) {
      const mapH = 330;
      const reveal = !endless && !isDailyId(this.levelId);
      const map = this.buildFailMap(level, innerW, mapH, reveal);
      map.root.setPosition(cx - innerW / 2, cur);
      map.root.setDepth(2);
      items.push(map.root);
      cur += mapH + 14;
      // legend row
      const keyY = cur;
      const entries: { label: string; color: number; shape: 'ring' | 'x' }[] = [
        { label: t('result_map_tapped'), color: 0xff5577, shape: 'ring' },
        { label: t('result_map_broke'), color: 0xffd070, shape: 'x' },
      ];
      if (reveal) entries.push({ label: t('result_map_answer'), color: 0x66ffaa, shape: 'ring' });
      const keyG = this.add.graphics().setDepth(2);
      keyG.setPosition(0, keyY + 16);
      const slot = innerW / entries.length;
      const x0 = cx - innerW / 2;
      entries.forEach((en, i) => {
        const kx = x0 + slot * i + 14;
        keyG.lineStyle(4, en.color, 1);
        if (en.shape === 'ring') keyG.strokeCircle(kx, 0, 11);
        else { keyG.lineBetween(kx - 9, -9, kx + 9, 9); keyG.lineBetween(kx - 9, 9, kx + 9, -9); }
        const lab = this.add.text(kx + 22, keyY + 16, en.label, {
          fontFamily: SANS, fontSize: '24px', color: UI.textDim, fontStyle: '700',
        }).setOrigin(0, 0.5).setDepth(2);
        if (lab.width > slot - 44) lab.setScale((slot - 44) / lab.width);
        items.push(lab);
      });
      items.push(keyG);
      cur += 58;
      if (reveal) {
        const showBtn = this.add.text(cx, cur + 34, t('result_show_answer'), {
          fontFamily: SANS, fontSize: '28px', color: UI.accentHex, fontStyle: '700',
          padding: { x: 24, y: 22 },
        }).setOrigin(0.5).setDepth(2).setInteractive({ useHandCursor: true });
        showBtn.on('pointerdown', () => {
          map.answer.setVisible(true);
          showBtn.destroy();
          const i = items.indexOf(showBtn); if (i >= 0) items.splice(i, 1);
        });
        items.push(showBtn);
        cur += 84;
      } else cur += 10;
    }

    // Coaching hint on a plain fail
    if (!isWin && !runOver) {
      const hint = suggestFix(this.result.failure!);
      text(clean(t('result_try_label')).toUpperCase(), 24, UI.warm, 8, '800');
      text(hint, 28, UI.textDim, 26, '400', { lineSpacing: 8 });
    }

    // === BUTTONS === stacked: one clear primary, one quieter secondary.
    const chapterId = getChapterForLevel(this.levelId)?.id;
    const backToList = () => {
      this.scene.stop();
      if (chapterId) this.scene.start('Chapter', { chapterId });
      else this.scene.start('Menu');
    };
    this.onBack = backToList;
    const bw = innerW;
    const addBtn = (label: string, color: number, onClick: () => void, primary: boolean) => {
      const h = primary ? 128 : 124;
      const b = uiButton(this, cx, cur + h / 2, bw, h, label, color, onClick, { primary, size: primary ? 36 : 30 });
      track(b);
      cur += h + 20;
    };
    cur += 6;
    if (isWin) {
      const next = this.nextLevelId();
      if (next) addBtn(t('next'), UI.signal, () => this.go(next, chapterId), true);
      else addBtn(t('sector'), UI.signal, backToList, true);
      if (isDailyId(this.levelId)) addBtn(t('share_button'), UI.cool, () => this.shareDaily(), false);
      else addBtn(t('replay'), UI.accent, () => this.go(this.levelId, chapterId), false);
    } else if (runOver) {
      text(AD_COPY.life_note, 24, UI.textDim, 14, '400', { lineSpacing: 6 });
      addBtn(t('watch_for_life'), UI.signal, async () => {
        const ok = await Ads.rewarded(this);
        if (ok) {
          Progress.addLife();
          Progress.recordLifeFromAd();
          this.go(this.levelId, chapterId, true); // just watched an ad: skip interstitial
        }
      }, true);
      addBtn(t('bank_run'), UI.accent, backToList, false);
    } else {
      addBtn(t('retry'), UI.accent, () => this.go(this.levelId, chapterId), true);
      addBtn(t('sector'), UI.accent, backToList, false);
    }
    cur += 24;

    // Centre the card, draw the panel under the content.
    const cardH = cur;
    const top = Math.round((height - cardH) / 2);
    const panel = new Panel(this, cx - cardW / 2, top, cardW, cardH, {
      fill: 0x0b1226, fillAlpha: 0.99, stroke: accent, strokeAlpha: 0.8,
    });
    panel.gfx.setDepth(1);
    items.forEach((o) => { (o as unknown as { y: number }).y += top; });

    if (!calm) {
      panel.gfx.setScale(0.96);
      this.tweens.add({ targets: panel.gfx, scale: 1, duration: 240, ease: 'Cubic.easeOut' });
      headlineText.setScale(1.12);
      this.tweens.add({ targets: headlineText, scale: 1, duration: 360, ease: 'Back.easeOut' });
      items.forEach((o, i) => {
        const it = o as unknown as { y: number; alpha: number };
        const y1 = it.y;
        it.y = y1 + 26;
        it.alpha = 0;
        this.tweens.add({ targets: it, y: y1, alpha: 1, duration: 260, delay: 50 + Math.min(i * 28, 240), ease: 'Cubic.easeOut' });
      });
    }

    drawWatermark(this);

    // Achievements checkpoint: Progress is already committed by GameScene.
    const fresh = checkAndUnlock();
    if (fresh.length) this.showAchievementBanner(fresh.map(a => a.title));
  }

  /**
   * The board drawn small. Cascade reach is shown by how bright each node is,
   * the tapped starter has a red ring, the failure point an amber cross, and
   * (campaign only, opt in) the starters that win get a green ring.
   */
  private buildFailMap(level: LevelDef, w: number, h: number, withAnswer: boolean) {
    const root = this.add.container(0, 0);
    const xs = level.nodes.map((n) => n.x), ys = level.nodes.map((n) => n.y);
    const minX = Math.min(...xs), maxX = Math.max(...xs), minY = Math.min(...ys), maxY = Math.max(...ys);
    const pad = 34;
    const sc = Math.min((w - pad * 2) / Math.max(1, maxX - minX), (h - pad * 2) / Math.max(1, maxY - minY), 0.7);
    const ox = (w - (maxX - minX) * sc) / 2, oy = (h - (maxY - minY) * sc) / 2;
    const P = (n: { x: number; y: number }) => ({ x: ox + (n.x - minX) * sc, y: oy + (n.y - minY) * sc });
    const byId = new Map(level.nodes.map((n) => [n.id, n]));
    const R = 15;

    const panel = new Panel(this, 0, 0, w, h, { fill: 0x070b18, fillAlpha: 1, stroke: UI.hairline, strokeAlpha: 1, radius: 18 });
    const g = this.add.graphics();
    root.add([panel.gfx, g]);

    const f = this.result.failure;
    const badWire = f && f.kind === 'forbidden_trigger' ? f.wireId : undefined;
    for (const wire of level.wires) {
      const a = byId.get(wire.a), b = byId.get(wire.b);
      if (!a || !b) continue;
      const pa = P(a), pb = P(b);
      const bad = wire.id === badWire;
      g.lineStyle(bad ? 6 : 3, bad ? 0xffd070 : wireStyle(wire.type ?? 'normal').color, bad ? 1 : 0.55);
      g.lineBetween(pa.x, pa.y, pb.x, pb.y);
      if (bad) {
        const mx = (pa.x + pb.x) / 2, my = (pa.y + pb.y) / 2;
        g.lineStyle(5, 0xffd070, 1);
        g.lineBetween(mx - 11, my - 11, mx + 11, my + 11); g.lineBetween(mx - 11, my + 11, mx + 11, my - 11);
      }
    }
    for (const n of level.nodes) {
      const p = P(n), st = nodeStyle(n.type);
      const reached = this.result.collapsed.has(n.id);
      g.fillStyle(st.fill, 1); g.fillCircle(p.x, p.y, R);
      g.lineStyle(3, reached ? st.ringActive : st.ring, reached ? 1 : 0.45);
      g.strokeCircle(p.x, p.y, R);
      if (reached) { g.fillStyle(st.ringActive, 0.9); g.fillCircle(p.x, p.y, 5); }
    }
    // tapped starter
    const tapped = byId.get(this.result.starterId);
    if (tapped) { const p = P(tapped); g.lineStyle(5, 0xff5577, 1); g.strokeCircle(p.x, p.y, R + 9); }
    // where it broke
    const breakId = f && 'nodeId' in f ? f.nodeId : f && 'coreId' in f ? f.coreId : undefined;
    const bn = breakId ? byId.get(breakId) : undefined;
    if (bn) {
      const p = P(bn), r = R + 4;
      g.lineStyle(5, 0xffd070, 1);
      g.lineBetween(p.x - r, p.y - r, p.x + r, p.y + r); g.lineBetween(p.x - r, p.y + r, p.x + r, p.y - r);
    }
    // winners, hidden until asked for
    const answer = this.add.graphics();
    if (withAnswer) {
      answer.lineStyle(5, 0x66ffaa, 1);
      for (const n of level.nodes.filter(isBreachable)) {
        if (simulate(level, n.id).outcome !== 'win') continue;
        const p = P(n); answer.strokeCircle(p.x, p.y, R + 9);
      }
    }
    answer.setVisible(false);
    root.add(answer);
    root.setData('rect', { w, h });
    return { root, answer };
  }

  /** Slide-in banner announcing newly-unlocked achievements (first + "+N"). */
  private showAchievementBanner(titles: string[]) {
    const { width } = this.scale;
    const headline = titles[0] + (titles.length > 1 ? `  +${titles.length - 1}` : '');
    const bw = Math.min(width - 96, 900);
    const root = this.add.container(width / 2, -120).setDepth(4000);
    const panel = new Panel(this, -bw / 2, -60, bw, 120, {
      fill: 0x0b1226, fillAlpha: 0.99, stroke: 0xffd070, strokeAlpha: 0.9,
    });
    const label = this.add.text(0, -24, t('ach_new'), {
      fontFamily: SANS, fontSize: '24px', color: UI.warm, fontStyle: '800',
    }).setOrigin(0.5);
    const title = this.add.text(0, 18, headline, {
      fontFamily: SANS, fontSize: '32px', color: UI.text, fontStyle: '800',
    }).setOrigin(0.5);
    if (title.width > bw - 60) title.setScale((bw - 60) / title.width);
    root.add([panel.gfx, label, title]);
    this.tweens.add({ targets: root, y: 110, duration: 380, ease: 'Cubic.easeOut' });
    this.tweens.add({
      targets: root, y: -120, delay: 2600, duration: 320, ease: 'Cubic.easeIn',
      onComplete: () => root.destroy(),
    });
  }

  /** Copy a Wordle-style daily result to the clipboard. */
  private shareDaily() {
    const key = dailyKeyFromId(this.levelId);
    const streak = Progress.dailyStreak();
    const text = dailyShareText(dailyDateLabel(key), streak);
    const ok = () => this.flashMessage(t('share_copied'));
    try {
      if (navigator?.clipboard?.writeText) { navigator.clipboard.writeText(text).then(ok).catch(() => ok()); }
      else ok();
    } catch { ok(); }
  }

  /** Brief centred confirmation toast. */
  private flashMessage(msg: string) {
    const { width, height } = this.scale;
    const txt = this.add.text(width / 2, height * 0.12, msg, {
      fontFamily: SANS, fontSize: '30px', color: UI.coolHex, fontStyle: '700',
      backgroundColor: '#0b1226', padding: { x: 28, y: 18 },
    }).setOrigin(0.5).setDepth(4100);
    this.tweens.add({
      targets: txt, alpha: { from: 0, to: 1 }, duration: 200,
      onComplete: () => this.tweens.add({ targets: txt, alpha: 0, delay: 1200, duration: 400, onComplete: () => txt.destroy() }),
    });
  }

  /**
   * Leave the result for another level. For endless transitions this is where
   * interstitials fire (cadence-gated) so an ad never interrupts mid-puzzle.
   */
  private async go(levelId: string, chapterId?: string, skipAd = false) {
    if (!skipAd && isProceduralId(levelId) && Progress.interstitialDue({
      nextEndlessIndex: proceduralIndex(levelId),
      nextIsTutorial: getLevel(levelId)?.tier === 'tutorial',
      isReplay: levelId === this.levelId,
    })) {
      await Ads.interstitial(this);
    }
    this.scene.stop();
    this.scene.start('Game', { levelId, chapterId });
  }

  private collapsedCount(): number {
    return this.result.collapsed.size;
  }

  private nextLevelId(): string | undefined {
    // Endless levels chain forever.
    if (isProceduralId(this.levelId)) return 'p' + (proceduralIndex(this.levelId) + 1);
    // Walk in manifest order (chapter-by-chapter) rather than LEVELS-array
    // order so progression respects how the UI organizes levels.
    const order = orderedLevelIds();
    const idx = order.indexOf(this.levelId);
    if (idx < 0 || idx >= order.length - 1) return undefined;
    return order[idx + 1];
  }
}
